/**
 * Helper functions for city extraction logic.
 * These functions extract common patterns used across multiple extraction priorities.
 */

import { CityFormData } from '@/types/city';
import City from '@/models/city';
import { debug } from '@/utils/logger';
import { extractShortFormatAddress } from '@/utils/cityUtils';
import type { UserDefinedDestination } from './cityExtractionTypes';

/**
 * Extracts city name from a user-defined destination.
 * Priority: label > name (first part) > empty string
 * 
 * @param destination - User-defined destination object
 * @returns City name string
 */
export function extractCityNameFromDestination(
  destination: UserDefinedDestination
): string {
  // Priority 1: label field (city name only)
  if (typeof destination.label === 'string' && destination.label.trim()) {
    return destination.label.trim();
  }
  
  // Priority 2: Extract from name field (full identifying name)
  const fullName = typeof destination.name === 'string' ? destination.name : '';
  if (fullName) {
    const firstPart = fullName.split(',')[0]?.trim();
    if (firstPart) {
      return firstPart;
    }
  }
  
  return '';
}

/**
 * Extracts geoposition from a user-defined destination.
 * Handles both GeoJSON format (geoposition.coordinates) and legacy format (lat/lng).
 * 
 * @param destination - User-defined destination object
 * @returns Geoposition object or undefined
 */
export function extractGeopositionFromDestination(
  destination: UserDefinedDestination
): { lat: number; long: number } | undefined {
  // Priority 1: GeoJSON format (MongoDB format: [longitude, latitude])
  if (destination.geoposition && typeof destination.geoposition === 'object') {
    const geo = destination.geoposition;
    if (geo.type === 'Point' && Array.isArray(geo.coordinates) && geo.coordinates.length >= 2) {
      const lng = geo.coordinates[0];
      const lat = geo.coordinates[1];
      if (typeof lat === 'number' && typeof lng === 'number') {
        return { lat, long: lng };
      }
    }
  }
  
  // Priority 2: Legacy format (lat/lng properties)
  if (typeof destination.lat === 'number' && typeof destination.lng === 'number') {
    return { lat: destination.lat, long: destination.lng };
  }
  
  return undefined;
}

/**
 * Constructs a complete identifying name from city name and full name.
 * Handles cases where the full name may be missing the city name or state.
 * 
 * @param cityName - City name (e.g., "New York")
 * @param fullName - Full identifying name (e.g., "New York, United States")
 * @returns Complete identifying name (e.g., "New York, New York, United States")
 */
export function constructCompleteIdentifyingName(
  cityName: string,
  fullName: string
): string {
  if (!fullName && cityName) {
    return cityName;
  }
  
  if (!cityName) {
    return fullName;
  }
  
  const fullNameLower = fullName.toLowerCase().trim();
  const cityNameLower = cityName.toLowerCase().trim();
  const nameParts = fullName.split(',').map(part => part.trim());
  
  // If fullName doesn't start with city name, prepend it
  if (!fullNameLower.startsWith(cityNameLower)) {
    return `${cityName}, ${fullName}`;
  }
  
  // If fullName has only 2 parts (city, country) - missing state
  // Example: "New York, United States" -> "New York, New York, United States"
  if (nameParts.length === 2 && nameParts[0].toLowerCase() === cityNameLower) {
    const country = nameParts[1];
    return `${cityName}, ${cityName}, ${country}`;
  }
  
  // Already complete (city, state, country) or other format
  return fullName;
}

/**
 * Determines display address from a3_intl_address and complete name.
 * Prioritizes short format a3_intl_address, falls back to complete name if not short format.
 * 
 * @param a3IntlAddress - Short format address (e.g., "New York, NY, USA")
 * @param completeName - Complete identifying name (e.g., "New York, New York, United States")
 * @returns Display address string
 */
export function determineDisplayAddress(
  a3IntlAddress: string,
  completeName: string
): string {
  if (!a3IntlAddress) {
    return completeName;
  }
  
  // Check if a3_intl_address is short format (has state abbreviation like ", NY, ")
  const isShortFormat = /,\s*[A-Z]{2}\s*,/.test(a3IntlAddress);
  
  if (isShortFormat) {
    return a3IntlAddress;
  }
  
  // Not short format, use complete name
  debug('[determineDisplayAddress] a3_intl_address is not short format, using completeName', {
    a3IntlAddress,
    completeName,
    isShortFormat,
  });
  
  return completeName;
}

/**
 * Extracts city data from a City model instance.
 * Handles short format address extraction and coordinate conversion.
 * 
 * @param city - City model instance
 * @returns CityFormData object
 */
export function extractCityFromCityModel(city: City): CityFormData {
  const cityName = city.short_name || city.long_name?.split(',')[0]?.trim() || '';
  const identifyingName = city.identifying_name || '';
  
  // Ensure identifying_name is complete (includes city name)
  const completeIdentifyingName = identifyingName && cityName
    ? (identifyingName.toLowerCase().startsWith(cityName.toLowerCase())
        ? identifyingName
        : `${cityName}, ${identifyingName}`)
    : identifyingName || city.long_name || '';

  // Extract short format address
  const shortName = city.short_name || '';
  const isShortNameShortFormat = /,\s*[A-Z]{2}\s*,/.test(shortName);
  
  debug('[extractCityFromCityModel] Extracting short format for Lists:', {
    short_name: shortName,
    isShortNameShortFormat,
    long_name: city.long_name,
    hasGooglePlacesData: !!city.google_places_data,
  });
  
  let shortFormatAddress = '';
  if (isShortNameShortFormat) {
    shortFormatAddress = shortName;
    debug('[extractCityFromCityModel] Using short_name as short format:', shortFormatAddress);
  } else {
    const googlePlacesData = city.google_places_data as Record<string, unknown> | undefined;
    const fallbackAddress = city.long_name || completeIdentifyingName;
    shortFormatAddress = extractShortFormatAddress(googlePlacesData, fallbackAddress);
    
    debug('[extractCityFromCityModel] Extracted from google_places_data:', {
      shortFormatAddress,
      fallbackAddress,
      isShortFormat: /,\s*[A-Z]{2}\s*,/.test(shortFormatAddress),
    });
  }
  
  debug('[extractCityFromCityModel] Final a3_intl_address:', {
    a3_intl_address: shortFormatAddress,
    isShortFormat: /,\s*[A-Z]{2}\s*,/.test(shortFormatAddress),
  });

  const result: CityFormData = {
    _id: city._id,
    city: cityName || city.short_name || city.long_name || '',
    name: city.short_name,
    city_identifying_name: completeIdentifyingName,
    a3_intl_address: shortFormatAddress,
    long_name: city.long_name,
    google_places_data: city.google_places_data as CityFormData['google_places_data'],
    photos: city.photos as Array<{ name?: string; photo_reference?: string; url?: string; image_url?: string }> | undefined,
  };

  // Extract coordinates if available
  if (city.coordinates) {
    result.geo_position = {
      lat: city.coordinates.latitude,
      long: city.coordinates.longitude,
    };
    result.geoposition = {
      type: 'Point' as const,
      coordinates: [city.coordinates.longitude, city.coordinates.latitude] as [number, number],
    };
  }

  return result;
}

/**
 * Extracts city data from a plain object (not a City model instance).
 * 
 * @param cityObj - Plain object with city data
 * @returns CityFormData object
 */
export function extractCityFromPlainObject(
  cityObj: Record<string, unknown>
): CityFormData {
  const cityName = (typeof cityObj.short_name === 'string' ? cityObj.short_name : '') ||
                  (typeof cityObj.long_name === 'string' ? cityObj.long_name.split(',')[0]?.trim() : '') ||
                  (typeof cityObj.city === 'string' ? cityObj.city : '');
  const identifyingName = (typeof cityObj.identifying_name === 'string' ? cityObj.identifying_name : '') ||
                          (typeof cityObj.city_identifying_name === 'string' ? cityObj.city_identifying_name : '');
  
  // Ensure identifying_name is complete
  const completeIdentifyingName = identifyingName && cityName
    ? (identifyingName.toLowerCase().startsWith(cityName.toLowerCase())
        ? identifyingName
        : `${cityName}, ${identifyingName}`)
    : identifyingName || (typeof cityObj.long_name === 'string' ? cityObj.long_name : '');

  // Extract coordinates
  let geoPosition: { lat: number; long: number } | undefined;
  if (cityObj.coordinates && typeof cityObj.coordinates === 'object') {
    const coords = cityObj.coordinates as Record<string, unknown>;
    if (typeof coords.latitude === 'number' && typeof coords.longitude === 'number') {
      geoPosition = {
        lat: coords.latitude,
        long: coords.longitude,
      };
    }
  }

  return {
    _id: typeof cityObj._id === 'string' ? cityObj._id : undefined,
    city: cityName,
    name: typeof cityObj.short_name === 'string' ? cityObj.short_name : undefined,
    city_identifying_name: completeIdentifyingName,
    a3_intl_address: (typeof cityObj.short_name === 'string' ? cityObj.short_name : '') ||
                    (typeof cityObj.a3_intl_address === 'string' ? cityObj.a3_intl_address : '') ||
                    (typeof cityObj.long_name === 'string' ? cityObj.long_name : '') ||
                    completeIdentifyingName,
    long_name: typeof cityObj.long_name === 'string' ? cityObj.long_name : undefined,
    google_places_data: cityObj.google_places_data as CityFormData['google_places_data'],
    photos: cityObj.photos as CityFormData['photos'],
    ...(geoPosition && { geo_position: geoPosition }),
  };
}
